import React, { FC } from 'react';
import { PageLoader } from '@/Shared';
import { useAppSelector } from '@/OneLiners';
import { ChallengeHelper } from '@/Domains';
import { Navigate } from 'react-router-dom';

interface EnigmaGuardProps {
	children: JSX.Element;
}

/**
 * EnigmaGuard
 * -------------
 * Route guard responsible for protecting access to routes that require
 * a validated security challenge (ENIGMA).
 *
 * This guard enforces a strict access flow:
 * 1. While the challenge state is being resolved, a loading screen is displayed.
 * 2. If the challenge exists but is not validated, the user is redirected.
 * 3. Only validated challenges allow access to the protected content.
 *
 * Design principles:
 * - Declarative routing using `<Navigate />`
 * - No side effects or imperative navigation
 * - Safe with React StrictMode
 *
 * Typical usage:
 * <EnigmaGuard>
 *   <LoginLayout />
 * </EnigmaGuard>
 *
 * Author: Samano CASTRE
 * Created: July 16, 2025
 */
const EnigmaGuard: FC<EnigmaGuardProps> = ({ children }: EnigmaGuardProps) => {

	/**
	 * Retrieves the current user challenge from the session state.
	 * - `undefined` means the challenge is still being initialized.
	 * - A defined value represents an active challenge lifecycle.
	 */
	const userChallenge = useAppSelector((state) => state.sessionState.userChallenge);

	const appIsReady = useAppSelector(state => state.appState.appIsReady);
	
	if (!appIsReady) {
		return <PageLoader message="[Captcha] - Vérifiaction en cours. Veuillez patienter. Merci" />;
	}

	/**
	 * If the challenge exists but has not been validated,
	 * redirect the user to the challenge entry point.
	 */
	if (!userChallenge) {
		return <PageLoader message='[AMAT] - Vérification de sécurité en cours ENIGMA...' />;
	}

	/**
	 * While the challenge is not yet available, block rendering and
	 * display a loader to prevent premature navigation or UI flicker.
	 */
	if (!ChallengeHelper.isValidated(userChallenge)) {
		return <Navigate to="/" replace />;
	}


	/**
	 * The challenge is validated:
	 * render the protected content.
	 */
	return children;
};

export default EnigmaGuard;
